import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import PremisesSelect from './PremisesSelect';
import AggNodeSelect from './AggNodeSelect';
import '../styles/structure-form.css';
/**
 * Modern React form component for creating structures
 * Replaces legacy Bootstrap forms with consistent design system
 */
export const StructureForm = ({ formData, formType, gridCoords, onSuccess, onError, onBack, onRemove, }) => {
    const [formValues, setFormValues] = useState(() => {
        const initial = {};
        formData.fields.forEach(field => {
            if (field.value !== undefined) {
                initial[field.name] = field.value;
            }
        });
        return initial;
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleChange = (e) => {
        const { name, value, type } = e.target;
        const newValue = type === 'checkbox' ? e.target.checked : value;
        setFormValues(prev => ({
            ...prev,
            [name]: newValue
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };
    const handleFieldValueChange = (name, value) => {
        setFormValues(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };
    const validateForm = () => {
        const newErrors = {};
        formData.fields.forEach(field => {
            if (field.type === 'hidden')
                return;
            const value = formValues[field.name];
            // Required validation
            if (field.required && !value) {
                newErrors[field.name] = `${field.label || field.name} is required`;
                return;
            }
            // Pattern validation
            if (field.pattern && value) {
                const regex = new RegExp(field.pattern);
                if (!regex.test(value)) {
                    newErrors[field.name] = `Invalid format for ${field.label || field.name}`;
                }
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setIsSubmitting(true);
        try {
            const submitData = {
                ...formValues,
                easting: gridCoords.easting,
                northing: gridCoords.northing,
            };
            const response = await fetch(`${formData.endPoint}/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(submitData),
            });
            const responseText = await response.text();
            let result = {};
            try {
                result = responseText ? JSON.parse(responseText) : {};
            }
            catch {
                if (!response.ok) {
                    throw new Error(`Server error while saving the structure (${response.status})`);
                }
                throw new Error('The server returned an invalid response');
            }
            if (result.result === 'OK' || response.ok) {
                onSuccess?.(result);
            }
            else {
                throw new Error(result.error || 'Submission failed');
            }
        }
        catch (error) {
            console.error('Form submission error:', error);
            onError?.(error);
            setErrors({ _form: error.message || 'An error occurred' });
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const renderField = (field) => {
        if (field.type === 'hidden') {
            return (_jsx("input", { type: "hidden", name: field.name, value: formValues[field.name] || field.value || '' }, field.name));
        }
        const fieldValue = formValues[field.name] || field.value || '';
        return (_jsxs("div", { className: "structure-form__field", children: [_jsxs("label", { htmlFor: field.name, className: "structure-form__label", children: [field.label, field.required && _jsx("span", { className: "structure-form__required", children: "*" })] }), _jsx("div", { className: "structure-form__input-wrapper", children: field.type === 'premises-select' ? (_jsx(PremisesSelect, { id: field.name, name: field.name, easting: gridCoords.easting, northing: gridCoords.northing, value: fieldValue, onChange: value => handleFieldValueChange(field.name, value), disabled: isSubmitting })) : field.type === 'agg-node-select' ? (_jsx(AggNodeSelect, { id: field.name, name: field.name, easting: gridCoords.easting, northing: gridCoords.northing, value: fieldValue, onChange: value => handleFieldValueChange(field.name, value), disabled: isSubmitting })) : field.type === 'select' ? (_jsx("select", { id: field.name, name: field.name, className: "structure-form__input structure-form__select", value: fieldValue, onChange: handleChange, required: field.required, children: field.options?.map(opt => (_jsx("option", { value: opt.value, children: opt.text }, opt.value))) })) : field.type === 'textarea' ? (_jsx("textarea", { id: field.name, name: field.name, className: "structure-form__input structure-form__textarea", value: fieldValue, onChange: handleChange, required: field.required, rows: field.rows || 3, placeholder: field.placeholder })) : field.type === 'checkbox' ? (_jsx("input", { id: field.name, type: "checkbox", name: field.name, className: "structure-form__checkbox", checked: !!fieldValue, onChange: handleChange })) : (_jsx("input", { id: field.name, type: field.type, name: field.name, className: "structure-form__input", value: fieldValue, onChange: handleChange, required: field.required, pattern: field.pattern, placeholder: field.placeholder, title: field.hint })) }), field.hint && (_jsx("div", { className: "structure-form__hint-text", children: field.hint })), errors[field.name] && (_jsx("div", { className: "structure-form__error", children: errors[field.name] }))] }, field.name));
    };
    return (_jsxs("div", { className: "structure-form", children: [_jsxs("div", { className: "structure-form__header", children: [_jsx("div", { className: "structure-form__icon", children: _jsx("i", { className: getIconForType(formType) }) }), _jsxs("div", { className: "structure-form__title", children: [_jsxs("h3", { children: ["New ", formData.title] }), _jsx("p", { className: "structure-form__subtitle", children: "Fill in the details below" })] })] }), _jsxs("form", { onSubmit: handleSubmit, className: "structure-form__form", children: [_jsx("div", { className: "structure-form__fields", children: formData.fields.map(field => renderField(field)) }), errors._form && (_jsx("div", { className: "structure-form__error structure-form__error--global", children: errors._form })), _jsxs("div", { className: "structure-form__footer", children: [onBack && (_jsxs("button", { type: "button", className: "structure-form__footer-btn structure-form__footer-btn--secondary", onClick: onBack, disabled: isSubmitting, children: [_jsx("i", { className: "fas fa-arrow-left" }), _jsx("span", { children: "Back" })] })), _jsxs("button", { type: "button", className: "structure-form__footer-btn structure-form__footer-btn--danger", onClick: onRemove, disabled: isSubmitting, children: [_jsx("i", { className: "fas fa-times" }), _jsx("span", { children: "Remove" })] }), _jsx("button", { type: "submit", className: "structure-form__footer-btn structure-form__footer-btn--primary", disabled: isSubmitting, children: isSubmitting ? (_jsxs(_Fragment, { children: [_jsx("i", { className: "fas fa-spinner fa-spin" }), _jsx("span", { children: "Submitting..." })] })) : (_jsxs(_Fragment, { children: [_jsx("i", { className: "fas fa-check" }), _jsx("span", { children: "Submit" })] })) })] })] })] }));
};
function getIconForType(type) {
    const icons = {
        Pole: 'fas fa-grip-lines-vertical',
        Manhole: 'fas fa-square',
        JointBox: 'fas fa-box',
        TobyBox: 'fas fa-house-user',
        CTB: 'fas fa-plug',
        Cabinet: 'fas fa-server',
        PointOfInterest: 'fas fa-map-pin',
    };
    return icons[type] || 'fas fa-map-marker-alt';
}
